import type {
  ApiClient,
  ApiRequestError,
  CallArgs,
  Endpoint,
  EndpointResponse,
} from '@croissants/contracts';
import {
  type InfiniteData,
  keepPreviousData,
  type QueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { clearIntentKey, intentKey, requestFingerprint } from './idempotency';

/**
 * One query layer for every portal, driven by the endpoint DESCRIPTOR.
 *
 * `packages/contracts` already owns the wire: one zod schema is the server's
 * validation and the client's inferred type. What each frontend used to own on
 * top of that was a second, hand-written layer saying the same things again -
 * a cache key per endpoint, an invalidation list per write, an idempotency key
 * minted at each call site - and four apps wrote four of them. The cost was not
 * the duplication itself but what it hid: `scripts/check-write-refetch.mjs`
 * reads `useEndpointMutation(...)` call sites, so a portal whose writes were
 * plain `useMutation` was invisible to the gate written to protect it.
 *
 * So the key, the invalidation, the idempotency key, the cache-page `replaces`
 * optimisation and the cursor walk are stated ONCE, here, and every portal binds
 * them to its own client:
 *
 *   const { useEndpointQuery, useEndpointMutation } = createEndpointHooks(useApi);
 *
 * Nothing in this file knows how a portal authenticates, what its error
 * catalogue says, or which screens it has. It knows descriptors and a client.
 */

/** The request half of a call, as the key function reads it. */
interface CallShape {
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  body?: unknown;
}

/**
 * A key derived from the descriptor and the request, so two screens calling the
 * same endpoint with the same arguments share one cache entry and an
 * invalidation by path hits every variant of it.
 *
 * The BODY is part of it, because two reads are not always distinguished by a
 * path and a query string: `POST /g/quote` prices whatever basket it is handed,
 * and a key that ignored the body would have served one morning's total for the
 * next. Every GET carries none, so nothing but a POST-shaped read is affected.
 *
 * `extra` is for a fact the REQUEST does not carry at all but the answer depends
 * on. There is exactly one today and it is load-bearing: the bakery tablet's
 * language rides on `accept-language`, and `/b` answers product names in it, so
 * a key without the locale left the old language's names on screen for half a
 * minute after somebody switched the tablet in Settings.
 */
export function queryKeyFor(
  endpoint: Endpoint,
  args: unknown,
  extra: readonly unknown[] = [],
): readonly unknown[] {
  const shape = (args ?? {}) as CallShape;
  return [
    endpoint.method,
    endpoint.path,
    shape.params ?? null,
    shape.query ?? null,
    ...(shape.body === undefined ? [] : [shape.body]),
    ...extra,
  ];
}

/**
 * What a write names as stale: a whole endpoint, or one addressed record of it.
 *
 * `getTicket` for order A and `getTicket` for order B are two cache entries, and
 * a counter that reduced a line on A has told the platform nothing about B. The
 * addressed form is what keeps "the reads this write changes, named" true once
 * the key carries its parameters.
 */
export type EndpointTarget =
  | Endpoint
  | { readonly endpoint: Endpoint; readonly params?: Record<string, unknown> };

/** The key PREFIX an invalidation matches: every variant at or under it. */
export function targetKey(target: EndpointTarget): readonly unknown[] {
  if ('endpoint' in target) {
    const { endpoint, params } = target;
    return params === undefined
      ? [endpoint.method, endpoint.path]
      : [endpoint.method, endpoint.path, params];
  }
  return [target.method, target.path];
}

/**
 * A list a reply can be written into: any array a cached answer holds.
 *
 * `pageOf(...)` calls it `items`, and three partner reads do not - `listMembers`
 * answers `{ members: [...] }`, `listBakeryLocations` `{ locations: [...] }`,
 * `listProducts` `{ items: [...] }`. They are the same thing to a reader and to
 * this optimisation, so the array is FOUND rather than named: the one holding a
 * row of the reply's identity is the one patched, and a response holding none is
 * simply absent.
 */
function arrayFieldsOf(value: unknown): readonly [string, readonly unknown[]][] {
  if (typeof value !== 'object' || value === null) return [];
  return Object.entries(value).filter(([, held]) => Array.isArray(held)) as [
    string,
    readonly unknown[],
  ][];
}

function identityOf(row: unknown, identity: string): string | null {
  if (typeof row !== 'object' || row === null) return null;
  const held = (row as Record<string, unknown>)[identity];
  return typeof held === 'string' ? held : null;
}

/**
 * A list this write's reply IS a row of.
 *
 * `identity` is the field that names the row, because not every row calls it
 * `id`: a bakery-authored product is keyed on `itemId`, and patching by the
 * wrong field finds nothing and silently falls back to the refetch this exists
 * to avoid.
 */
export type ReplaceTarget = Endpoint | { readonly endpoint: Endpoint; readonly identity: string };

function replaceParts(target: ReplaceTarget): { endpoint: Endpoint; identity: string } {
  return 'endpoint' in target
    ? { endpoint: target.endpoint, identity: target.identity }
    : { endpoint: target, identity: 'id' };
}

/**
 * Writes `row` over the row of the same identity in every cached answer of
 * `endpoint`, and answers whether it could.
 *
 * The patched answer is checked against the LIST's own response schema before it
 * is stored, and that check is the load-bearing part rather than a belt on top
 * of one. Two endpoints one line apart in the same screen answer different
 * shapes - `suspendOrganization` replies with the summary row a list is made of,
 * `updateOrganization` with the fuller `InternalOrganizationInfo` - and nothing
 * in the type of either says which. Writing the second into a list would put a
 * shape on screen that its own contract forbids, silently, in the cache rather
 * than on the wire where `client.call` would have caught it. So the contract is
 * asked here too, and a reply that does not fit falls back to a refetch.
 */
export function patchCachedPage(
  endpoint: Endpoint,
  cached: unknown,
  row: unknown,
  identity = 'id',
):
  | { readonly outcome: 'patched'; readonly page: unknown }
  | { readonly outcome: 'absent' | 'refused' } {
  const id = identityOf(row, identity);
  if (id === null) return { outcome: 'refused' };

  /*
   * A cursor-walked read is cached as `{ pages, pageParams }`, and the row is
   * inside ONE of those pages. Without this the walk is always "absent" and
   * every `replaces` on a paged list quietly falls back to the refetch it
   * exists to avoid - which is the whole Apartments tab.
   */
  const walked = cached as { pages?: unknown; pageParams?: unknown } | null | undefined;
  if (Array.isArray(walked?.pages) && Array.isArray(walked.pageParams)) {
    const pages = [...walked.pages];
    for (let index = 0; index < pages.length; index += 1) {
      const result = patchOnePage(endpoint, pages[index], row, id, identity);
      if (result.outcome === 'refused') return { outcome: 'refused' };
      if (result.outcome === 'patched') {
        pages[index] = result.page;
        return { outcome: 'patched', page: { ...walked, pages } };
      }
    }
    return { outcome: 'absent' };
  }

  return patchOnePage(endpoint, cached, row, id, identity);
}

function patchOnePage(
  endpoint: Endpoint,
  cached: unknown,
  row: unknown,
  id: string,
  identity: string,
):
  | { readonly outcome: 'patched'; readonly page: unknown }
  | { readonly outcome: 'absent' | 'refused' } {
  for (const [field, held] of arrayFieldsOf(cached)) {
    if (!held.some((item) => identityOf(item, identity) === id)) continue;
    const page = {
      ...(cached as Record<string, unknown>),
      [field]: held.map((item) => (identityOf(item, identity) === id ? row : item)),
    };
    if (!endpoint.response.safeParse(page).success) return { outcome: 'refused' };
    return { outcome: 'patched', page };
  }
  return { outcome: 'absent' };
}

function replaceCachedRow(
  queryClient: QueryClient,
  targets: readonly ReplaceTarget[],
  row: unknown,
): readonly EndpointTarget[] {
  const unsatisfied: EndpointTarget[] = [];
  for (const target of targets) {
    const { endpoint, identity } = replaceParts(target);
    let patched = false;
    let refused = false;
    queryClient.setQueriesData<unknown>(
      { queryKey: [endpoint.method, endpoint.path] },
      (cached: unknown) => {
        const result = patchCachedPage(endpoint, cached, row, identity);
        if (result.outcome === 'patched') {
          patched = true;
          return result.page;
        }
        if (result.outcome === 'refused') refused = true;
        return cached;
      },
    );
    // Absent from the cache is fine - nothing on screen is stale. Refused is
    // not, and neither is a screen holding no page at all: both leave a reader
    // looking at the row as it was, so those fall back to a refetch.
    if (refused || !patched) unsatisfied.push(endpoint);
  }
  return unsatisfied;
}

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Where in the response the cursor page actually is.
 *
 * Most read endpoints here ARE the page - `{ items, nextCursor }` at the top
 * level - and those need nothing. Several of the internal ones carry it under a
 * key beside something else (`{ serverTime, rows: { items, nextCursor } }`),
 * and for those the cursor has to be pointed at, or the hook reads
 * `response.nextCursor`, finds `undefined`, and reports that there is no next
 * page. That failure is SILENT and looks exactly like a list that fits: the
 * Orders list shipped with `1-100 sur 100` and a dead Next over 817 orders
 * (2026-09-07). `cursorPageOf` is what turns the same mistake into a throw.
 */
export interface EndpointPagesOptions<T> extends EndpointQueryOptions<T> {
  readonly pageAt?: (response: T) => CursorPage;
}

export interface EndpointQueryOptions<T> {
  enabled?: boolean;
  /**
   * Milliseconds, or a decision taken from the answer in hand - a guest's order
   * polls only while it can still change, the admin dashboard always (OPS-001).
   * The callback is handed the DATA rather than TanStack's `Query`, so a screen
   * states its rule over the wire type it already knows.
   */
  refetchInterval?: number | false | ((data: T | undefined) => number | false);
  /**
   * Poll on a hidden tab too. The counter's alarm is the point of that app, and
   * a backgrounded tablet that stops polling stops alarming (BAK-022).
   */
  refetchIntervalInBackground?: boolean;
  staleTime?: number;
  /**
   * Keep showing the previous arguments' data while a new variant loads, with
   * `isFetching` saying a refresh is under way. For screens where the last
   * answer is a better placeholder than a spinner - the coverage map keeps its
   * picture while a recompute with a different exclusion set is in flight.
   */
  keepPreviousData?: boolean;
  /**
   * A retry budget, or a predicate over the failure. A definitive refusal - not
   * found, forbidden, a bad request - answers the same way every time, and
   * retrying it bought nothing but a longer window in which the query was
   * neither settled nor running (TST-001).
   */
  retry?: boolean | number | ((failureCount: number, error: ApiRequestError) => boolean);
  /** Extra key segments - see `queryKeyFor`. */
  key?: readonly unknown[];
  /** What to render before any request resolves: the tablet's on-device cache. */
  initialData?: T;
  initialDataUpdatedAt?: number;
  /**
   * Told about every failed attempt, before the error is re-thrown. The counter
   * raises its connection banner from this and nothing else (BAK-009).
   */
  onFetchError?: (error: unknown) => void;
}

/**
 * A read whose cached answer this write's reply already IS, written straight in.
 *
 * The cheap half of `replaces`: no row identity to find, because the reply is
 * the whole answer. `merge` exists for the replies that carry PART of it -
 * `setClosureDay` answers the shop's closure list and the settings screen holds
 * the settings around it.
 */
export interface EndpointUpdate<E extends Endpoint> {
  readonly endpoint: Endpoint;
  readonly params?: Record<string, unknown>;
  readonly merge?: (held: unknown, data: EndpointResponse<E>) => unknown;
}

/**
 * Mutations that must be invalidated together. The caller names the reads its
 * write affects; nothing here guesses, because a stale admin dashboard after a
 * kill switch is worse than an extra request.
 *
 * `replaces` and `updates` are the cheaper halves of the same idea, and exist
 * because measuring a button press showed the refetch costing more than the
 * write. An endpoint whose reply IS the updated row - `suspendBakeryLocation`
 * answers the shop it just changed - names the lists that row belongs to in
 * `replaces` instead, and the reply is written straight into their cached pages.
 * Suspending a shop on the Bakeries tab measured 1 719 ms end to end against the
 * demo instance, of which 722 ms was re-reading all 32 shops to learn what the
 * 997 ms reply had already said.
 *
 * The rule for choosing: `replaces` is correct only when the write changes
 * NOTHING in the list but that one row. Suspending a house darkens every shop
 * under it, so `SuspendHouseDialog` still invalidates the shop list - the row it
 * gets back is the house's, and the shops' rows are not in it.
 *
 * NONE of them blocks the button. The invalidation is started and deliberately
 * not awaited: a write is finished when the SERVER says so, and making the
 * operator wait on a READ afterwards is the defect, not the refresh. React Query
 * refetches only ACTIVE queries, so which mutations paid this depended on what
 * happened to be mounted; not awaiting removes the question rather than
 * answering it per screen.
 */
export interface EndpointMutationOptions<E extends Endpoint, V> {
  /**
   * The hook's own variables mapped onto the call. Without it the variables ARE
   * the call arguments, which is what most writes want; with it a screen passes
   * `{ locationId, patch }` and this decides where each half goes.
   */
  args?: (variables: V) => CallArgs<E>;
  /**
   * The idempotency key for this attempt (PAY-006, NFR-003). Required by the
   * client whenever the descriptor declares `idempotent`, and defaulted to ONE
   * KEY PER INTENT (`intentKey`, F5): the same request sent again - a re-click
   * after a lost answer - reuses its key and replays, an edited request gets a
   * new one, and a success releases it. A caller overrides it when the key must
   * outlive the page - the counter keys on the line and its TARGET so an outbox
   * replay cannot refund the same units twice, and the guest persists one per
   * attempt so a dropped checkout is the same order rather than a second one.
   */
  idempotencyKey?: (variables: V) => string;
  /**
   * The subject the default key is held for, beside the endpoint - a product
   * id, a device id - so two subjects' intents on one screen never share a slot.
   */
  idempotencyScope?: (variables: V) => string;
  /**
   * The rare write that does BROWSER work around its call and must still fail
   * as one: the QR sheet points the tab it reserved at the signed link, or
   * saves the file when a pop-up blocker refused even that, and either failure
   * has to reject the mutation rather than resolve it and throw afterwards.
   *
   * It is handed the call rather than the client, so the endpoint stays the one
   * at the call site - which is what keeps the write visible to
   * `scripts/check-write-refetch.mjs`. Everything else about the write, the
   * invalidation included, is still this layer's.
   */
  perform?: (
    variables: V,
    call: (args: CallArgs<E>) => Promise<EndpointResponse<E>>,
  ) => Promise<EndpointResponse<E>>;
  /** The reads this write changes, named - or decided from what it answered. */
  invalidates?:
    | readonly EndpointTarget[]
    | ((data: EndpointResponse<E>, variables: V) => readonly EndpointTarget[]);
  /** Lists whose row this reply IS. */
  replaces?: readonly ReplaceTarget[];
  /** Reads whose whole answer this reply is. */
  updates?: readonly EndpointUpdate<E>[];
  onSuccess?: (data: EndpointResponse<E>, variables: V) => void;
  onError?: (error: ApiRequestError, variables: V) => void;
}

/** Everything a cursor-paged endpoint promises: a page and where to resume. */
export interface CursorPage {
  readonly items: readonly unknown[];
  readonly nextCursor: string | null;
}

/**
 * The page, or a throw naming the endpoint that did not hand one over.
 *
 * A cursor read that cannot find its cursor has no safe answer: reporting "no
 * next page" is indistinguishable from a list that fits, so the reader is
 * simply told the truncation is the whole list. That is what shipped on the
 * operator's Orders list - `1-100 sur 100` with Next greyed out, over 817
 * orders - because the page sits under `rows` there and the hook read the top
 * level. It is a wiring mistake, it is always a mistake, and the only useful
 * moment to hear about it is the first render.
 */
export function cursorPageOf(endpoint: { readonly path: string }, page: CursorPage): CursorPage {
  if (!Array.isArray(page?.items)) {
    throw new Error(
      `${endpoint.path}: no cursor page here. The response does not carry ` +
        '`items`/`nextCursor` at the level this hook read. Point at it with ' +
        '`pageAt`, e.g. `pageAt: (response) => response.rows`.',
    );
  }
  return page;
}

// ---------------------------------------------------------------------------
// The layer
// ---------------------------------------------------------------------------

export interface EndpointHooks {
  useInvalidate(): (targets: readonly EndpointTarget[]) => Promise<void>;
  useEndpointQuery<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options?: EndpointQueryOptions<EndpointResponse<E>>,
  ): UseQueryResult<EndpointResponse<E>, ApiRequestError>;
  useEndpointPages<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options?: EndpointPagesOptions<EndpointResponse<E>>,
  ): UseInfiniteQueryResult<InfiniteData<EndpointResponse<E>>, ApiRequestError>;
  useEndpointAll<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options?: EndpointQueryOptions<EndpointResponse<E>> & { maxRequests?: number },
  ): UseQueryResult<EndpointResponse<E>, ApiRequestError>;
  useEndpointMutation<E extends Endpoint, V = CallArgs<E>>(
    endpoint: E,
    options?: EndpointMutationOptions<E, V>,
  ): UseMutationResult<EndpointResponse<E>, ApiRequestError, V>;
}

/**
 * Binds the layer to one portal's client.
 *
 * `useClient` is a hook, because that is how a signed-in portal reaches its
 * client: built ONCE per portal (`createApiProvider`, or the counter's session
 * provider), it reads the session and the locale at call time rather than
 * being rebuilt when either moves. A portal whose client is a module singleton
 * passes `() => api`.
 */
export function createEndpointHooks(useClient: () => ApiClient): EndpointHooks {
  /** Invalidates every cached query at or under each target's key. */
  function useInvalidate(): (targets: readonly EndpointTarget[]) => Promise<void> {
    const queryClient = useQueryClient();
    return async (targets) => {
      await Promise.all(
        targets.map((target) => queryClient.invalidateQueries({ queryKey: targetKey(target) })),
      );
    };
  }

  /** The TanStack options every read here shares, from the ones this layer names. */
  function readOptions<T>(options: EndpointQueryOptions<T>): Record<string, unknown> {
    const interval = options.refetchInterval;
    return {
      ...(options.enabled === undefined ? {} : { enabled: options.enabled }),
      ...(interval === undefined
        ? {}
        : {
            refetchInterval:
              typeof interval === 'function'
                ? (query: { state: { data: T | undefined } }) => interval(query.state.data)
                : interval,
          }),
      ...(options.refetchIntervalInBackground === undefined
        ? {}
        : { refetchIntervalInBackground: options.refetchIntervalInBackground }),
      ...(options.staleTime === undefined ? {} : { staleTime: options.staleTime }),
      ...(options.retry === undefined ? {} : { retry: options.retry }),
      ...(options.keepPreviousData === true ? { placeholderData: keepPreviousData } : {}),
      ...(options.initialData === undefined ? {} : { initialData: options.initialData }),
      ...(options.initialDataUpdatedAt === undefined
        ? {}
        : { initialDataUpdatedAt: options.initialDataUpdatedAt }),
    };
  }

  function useEndpointQuery<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options: EndpointQueryOptions<EndpointResponse<E>> = {},
  ): UseQueryResult<EndpointResponse<E>, ApiRequestError> {
    const client = useClient();
    const noteFailure = options.onFetchError;
    return useQuery<EndpointResponse<E>, ApiRequestError>({
      queryKey: queryKeyFor(endpoint, args, options.key),
      queryFn: async ({ signal }) => {
        try {
          return await client.call(endpoint, { ...args, signal });
        } catch (error) {
          noteFailure?.(error);
          throw error;
        }
      },
      ...readOptions(options),
    });
  }

  /**
   * A cursor-paged read, one page at a time, following the cursor on demand.
   *
   * Following the cursor is not optional - a `limit` without it would leave
   * everything past the first page unreachable - and `useInfiniteQuery` rather
   * than an accumulator in the screen because invalidation has to keep working:
   * pausing an apartment must refetch every page the reader has walked, not
   * silently keep stale rows below the first one.
   *
   * The cursor is passed in the query and is therefore NOT part of the key,
   * which is what makes the walked pages one cache entry rather than one per
   * press.
   */
  function useEndpointPages<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options: EndpointPagesOptions<EndpointResponse<E>> = {},
  ): UseInfiniteQueryResult<InfiniteData<EndpointResponse<E>>, ApiRequestError> {
    const client = useClient();
    const shape = (args ?? {}) as CallShape;
    const pageAt = options.pageAt ?? ((response: EndpointResponse<E>) => response as CursorPage);
    return useInfiniteQuery<
      EndpointResponse<E>,
      ApiRequestError,
      InfiniteData<EndpointResponse<E>>,
      readonly unknown[],
      string | undefined
    >({
      queryKey: queryKeyFor(endpoint, args, options.key),
      initialPageParam: undefined,
      queryFn: ({ pageParam, signal }) =>
        client.call(endpoint, {
          ...args,
          ...(pageParam === undefined
            ? {}
            : { query: { ...(shape.query ?? {}), cursor: pageParam } }),
          signal,
        } as CallArgs<E>),
      getNextPageParam: (lastPage) =>
        cursorPageOf(endpoint, pageAt(lastPage)).nextCursor ?? undefined,
      ...readOptions(options),
    });
  }

  /**
   * The WHOLE of a cursor-paged read, walked to the end, as one answer.
   *
   * For the screens that cannot show a truncated set - a filter missing the
   * apartment you are looking for is indistinguishable from that apartment
   * having no orders. The guard on the loop is a belt against a server bug
   * becoming a browser hang, and if it ever trips the cursor it stopped on is
   * RETURNED rather than swapped for a null: answering "that was all of them"
   * over a truncated walk is the defect this exists to avoid, one layer up.
   */
  function useEndpointAll<E extends Endpoint>(
    endpoint: E,
    args: CallArgs<E>,
    options: EndpointQueryOptions<EndpointResponse<E>> & { maxRequests?: number } = {},
  ): UseQueryResult<EndpointResponse<E>, ApiRequestError> {
    const client = useClient();
    const shape = (args ?? {}) as CallShape;
    const cap = options.maxRequests ?? 40;
    return useQuery<EndpointResponse<E>, ApiRequestError>({
      queryKey: queryKeyFor(endpoint, args, [...(options.key ?? []), 'all']),
      queryFn: async ({ signal }) => {
        const items: unknown[] = [];
        let cursor: string | undefined;
        for (let request = 0; request < cap; request += 1) {
          const page = (await client.call(endpoint, {
            ...args,
            ...(cursor === undefined ? {} : { query: { ...(shape.query ?? {}), cursor } }),
            signal,
          } as CallArgs<E>)) as CursorPage;
          items.push(...page.items);
          if (page.nextCursor === null) {
            return { items, nextCursor: null } as EndpointResponse<E>;
          }
          cursor = page.nextCursor;
        }
        return { items, nextCursor: cursor ?? null } as EndpointResponse<E>;
      },
      ...readOptions(options),
    });
  }

  function useEndpointMutation<E extends Endpoint, V = CallArgs<E>>(
    endpoint: E,
    options: EndpointMutationOptions<E, V> = {},
  ): UseMutationResult<EndpointResponse<E>, ApiRequestError, V> {
    const client = useClient();
    const invalidate = useInvalidate();
    const queryClient = useQueryClient();

    // The slot a default key is held in: this endpoint, and the subject when
    // the caller names one.
    const intentScope = (variables: V): string => {
      const subject = options.idempotencyScope?.(variables);
      const base = `${endpoint.mount}:${endpoint.method} ${endpoint.path}`;
      return subject === undefined ? base : `${base}#${subject}`;
    };
    const holdsIntent = endpoint.idempotent && options.idempotencyKey === undefined;

    return useMutation<EndpointResponse<E>, ApiRequestError, V>({
      mutationFn: (variables) => {
        const call = (args: CallArgs<E>): Promise<EndpointResponse<E>> =>
          client.call(endpoint, {
            ...args,
            ...(endpoint.idempotent
              ? {
                  idempotencyKey:
                    options.idempotencyKey?.(variables) ??
                    intentKey(intentScope(variables), requestFingerprint(args)),
                }
              : {}),
          });
        if (options.perform !== undefined) return options.perform(variables, call);
        return call(
          options.args === undefined ? (variables as CallArgs<E>) : options.args(variables),
        );
      },
      onSuccess: (data, variables) => {
        // Carried out: doing the same thing again is a new intent.
        if (holdsIntent) clearIntentKey(intentScope(variables));
        for (const update of options.updates ?? []) {
          const merge = update.merge;
          queryClient.setQueriesData<unknown>({ queryKey: targetKey(update) }, (held: unknown) =>
            merge === undefined ? data : merge(held, data),
          );
        }
        const missed =
          options.replaces === undefined
            ? []
            : replaceCachedRow(queryClient, options.replaces, data);
        const named =
          typeof options.invalidates === 'function'
            ? options.invalidates(data, variables)
            : (options.invalidates ?? []);
        const stale: EndpointTarget[] = [...named, ...missed];
        // Started, never awaited - see the note on EndpointMutationOptions.
        if (stale.length > 0) void invalidate(stale);
        options.onSuccess?.(data, variables);
      },
      ...(options.onError === undefined
        ? {}
        : { onError: (error, variables) => options.onError?.(error, variables) }),
    });
  }

  return { useInvalidate, useEndpointQuery, useEndpointPages, useEndpointAll, useEndpointMutation };
}
